import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../useAuth";
import { Users, LayoutDashboard, FilePlus, Key, BarChart3, X, BookOpen, Search } from "lucide-react";
import { T } from "../lib/i18n";
import { userEmail } from "../lib/helpers";
import { SESSION_STATUSES, sessionStatus, statusMeta } from "../lib/status";
import { dbLoadAllResults, dbLoadUserCourses, dbLoadVisibleTeacherSessions, dbLoadGroupActivity, dbLoadPendingAppealCounts, dbLoadSessionTeacherNames } from "../lib/db";
import { sortSessions, courseOptions, filterSessions, lastFinishedSession, nextScheduled, summarizeResults, loadSavedFilters, saveFilters } from "../lib/home";
import { scoreColor } from "../lib/helpers";

const PAGE_SIZE = 25;

const heroLabel = color => ({ fontFamily: "'DM Mono', monospace", fontSize: 10, color, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10 });
const heroRow = (divider, G) => ({
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%",
  background: "none", border: "none", borderTop: divider ? `1px solid ${G.border}` : "none",
  padding: "8px 0", cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans',sans-serif",
});
import { LabelText, AppHeader, Footer, PageHead } from "../components/common";
import { TeacherSessionList } from "./TRATEditor";

// ─────────────────────────────────────────
//  HOME
// ─────────────────────────────────────────
function HomeScreen({ onTeacher, onGroup, onDashboard, onHelp, lang, setLang, theme, setTheme, G, css }) {
  const [groupCode, setGroupCode] = useState("");
  const [dashCode, setDashCode] = useState("");
  const [mySessions, setMySessions] = useState([]);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [courseInstances, setCourseInstances] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [studentModalCode, setStudentModalCode] = useState("");
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [dashboardModalCode, setDashboardModalCode] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | live | review | archived
  const [courseFilter, setCourseFilter] = useState(() => loadSavedFilters().course);
  const [ownerFilter, setOwnerFilter] = useState(() => loadSavedFilters().owner); // all | mine
  const [shownCount, setShownCount] = useState(PAGE_SIZE);
  const [groupActivity, setGroupActivity] = useState({}); // sessionId -> { active, done }
  const [appealCounts, setAppealCounts] = useState({}); // sessionId -> obesvarade
  const [teacherNames, setTeacherNames] = useState({}); // sessionId -> skaparens namn
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = T[lang];

  // Listan visar alla tRAT:ar i de kurser man har tillgång till, inte bara
  // egna: lärare i samma kurs arbetar med varandras tRAT:ar.
  async function loadSessions() {
    const instances = await dbLoadUserCourses(user.id);
    setCourseInstances(instances);
    const sessions = await dbLoadVisibleTeacherSessions(user.id, instances.map(i => i.id));
    setMySessions(sessions);
    setSessionsLoaded(true);
  }
  useEffect(() => {
    if (!user) return;
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => { saveFilters({ course: courseFilter, owner: ownerFilter }); }, [courseFilter, ownerFilter]);
  useEffect(() => { setShownCount(PAGE_SIZE); }, [courseFilter, ownerFilter, statusFilter, searchQuery]);

  // course_instance_id → readable label, for the badge and search on tRAT rows,
  // and → course, for the course filter
  const courseLabelById = {};
  const instanceCourse = {};
  for (const inst of courseInstances) {
    courseLabelById[inst.id] = [inst.courses?.name, inst.name].filter(Boolean).join(" · ") || inst.name || "";
    if (inst.course_id) instanceCourse[inst.id] = { id: inst.course_id, name: inst.courses?.name || inst.name || "" };
  }

  // Group activity per session — status is based on what groups are actually
  // doing, not on session age (a day-old tRAT being played showed "Avslutad"
  // while a 5-minute-old empty one showed "Pågår")
  useEffect(() => {
    if (!user || mySessions.length === 0) { setGroupActivity({}); setAppealCounts({}); setTeacherNames({}); return; }
    const ids = mySessions.map(s => s.sessionId);
    dbLoadGroupActivity(ids).then(setGroupActivity);
    dbLoadPendingAppealCounts(ids).then(setAppealCounts);
    const colleagueIds = mySessions.filter(s => s.teacherId && s.teacherId !== user.id).map(s => s.sessionId);
    dbLoadSessionTeacherNames(colleagueIds).then(setTeacherNames);
  }, [user, mySessions]);

  // Shared status model (src/lib/status.js) — same words everywhere
  const statusForSession = (s) => sessionStatus({
    locked: s.sessionLocked,
    availableAt: s.availableAt,
    activeGroups: groupActivity[s.sessionId]?.active || 0,
    doneGroups: groupActivity[s.sessionId]?.done || 0,
  });

  // "Senast avslutad": den senaste tRAT:en som körts klart, och hur det gick
  const lastDone = lastFinishedSession(mySessions, statusForSession, s => (groupActivity[s.sessionId]?.done || 0) > 0);
  const [lastSummary, setLastSummary] = useState(null); // { sessionId, ...summarizeResults }
  useEffect(() => {
    if (!lastDone) { setLastSummary(null); return; }
    let cancelled = false;
    dbLoadAllResults(lastDone.sessionId).then(rows => {
      if (cancelled) return;
      const sum = summarizeResults(rows, lastDone.numQ);
      setLastSummary(sum ? { sessionId: lastDone.sessionId, ...sum } : null);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastDone?.sessionId]);

  // Compute totals once so all sections can reference them
  const total = mySessions.length;

  return (
    <div style={{ ...css.page, display: "flex", flexDirection: "column" }}>

      {/* Header only shown when NOT in teacher layout (i.e. no user) */}
      {!user && <AppHeader lang={lang} setLang={setLang} onHome={() => {}} theme={theme} setTheme={setTheme} onHelp={() => onHelp && onHelp('what')} G={G} css={css} />}

      <div style={{ flex: 1, width: "100%", maxWidth: 900, margin: "0 auto", padding: user ? "36px 20px 20px" : "48px 20px" }}>
        {/* Hero Section — visible only for unauthenticated visitors */}
        {!user && (() => {
          // Task 3: Split tagline so "tRAT-fasen" / "tRAT phase" becomes an inline link
          const phraseSv = "tRAT-fasen";
          const phraseEn = "tRAT phase";
          const phrase = lang === "sv" ? phraseSv : phraseEn;
          const tagline = t.homeTagline || "";
          const idx = tagline.indexOf(phrase);
          const before = idx >= 0 ? tagline.slice(0, idx) : tagline;
          const after = idx >= 0 ? tagline.slice(idx + phrase.length) : "";
          return (
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <h1 style={{ ...css.heading, fontSize: 38, lineHeight: 1.1, marginBottom: 12 }}>{t.appTitle}</h1>
              <p style={{ color: G.sub, fontSize: 16, lineHeight: 1.5, maxWidth: 600, margin: "0 auto" }}>
                {before}
                {idx >= 0 ? (
                  <button
                    onClick={() => setShowModal(true)}
                    style={{
                      background: "none", border: "none", padding: 0,
                      color: G.sub, fontSize: 16, fontFamily: "inherit",
                      textDecoration: "underline", textUnderlineOffset: 3,
                      textDecorationColor: G.muted, textDecorationThickness: 1,
                      cursor: "pointer",
                      transition: "color 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = G.text; e.currentTarget.style.textDecorationColor = G.text; }}
                    onMouseLeave={e => { e.currentTarget.style.color = G.sub; e.currentTarget.style.textDecorationColor = G.muted; }}
                  >
                    {phrase}
                  </button>
                ) : null}
                {after}
              </p>
            </div>
          );
        })()}

        {/* Editorial PageHead — logged-in teachers */}
        {user && (() => {
          // "Live" = groups are actually playing right now — i alla mina kurser
          const liveNow = mySessions.filter(s => statusForSession(s) === "live");
          const upcoming = liveNow.length === 0 ? nextScheduled(mySessions) : null;
          const ownSessions = mySessions.filter(s => s.teacherId === user.id);
          const lastSession = ownSessions.length > 0 ? [...ownSessions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] : null;
          const daysSinceLast = lastSession ? Math.floor((Date.now() - new Date(lastSession.createdAt).getTime()) / (24 * 60 * 60 * 1000)) : null;

          const displayName = user?.user_metadata?.full_name || userEmail(user)?.split("@")[0] || "";
          const firstName = displayName.split(" ")[0];

          // Smart welcome line (Task 7)
          let subText;
          if (firstName) {
            if (liveNow.length > 0) {
              subText = lang === "sv"
                ? `Välkommen tillbaka, ${firstName}. Du har ${liveNow.length} tRAT som pågår just nu.`
                : `Welcome back, ${firstName}. You have ${liveNow.length} tRAT running right now.`;
            } else if (daysSinceLast !== null && daysSinceLast < 7) {
              subText = lang === "sv"
                ? `Välkommen tillbaka, ${firstName}. Senaste tRAT var för ${daysSinceLast === 0 ? "idag" : daysSinceLast === 1 ? "1 dag sedan" : `${daysSinceLast} dagar sedan`}.`
                : `Welcome back, ${firstName}. Latest tRAT was ${daysSinceLast === 0 ? "today" : daysSinceLast === 1 ? "1 day ago" : `${daysSinceLast} days ago`}.`;
            } else {
              subText = lang === "sv" ? `Välkommen tillbaka, ${firstName}.` : `Welcome back, ${firstName}.`;
            }
          } else {
            subText = lang === "sv" ? "Dina tRAT:er och senaste aktivitet." : "Your tRATs and recent activity.";
          }

          return (
            <>
              <PageHead
                title={lang === "sv" ? "Översikt" : "Overview"}
                sub={subText}
                action={
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    {/* Secondary: Join as student (only if not onboarding) */}
                    {total > 0 && (
                      <button
                        onClick={() => setShowStudentModal(true)}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          background: "transparent", color: G.text,
                          border: `1px solid ${G.border}`,
                          borderRadius: 8, padding: "9px 14px", fontSize: 14, fontWeight: 500,
                          fontFamily: "'DM Sans',sans-serif", cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = G.text; e.currentTarget.style.background = G.surface2; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = G.border; e.currentTarget.style.background = "transparent"; }}
                      >
                        <Users size={14} />
                        {t.joinAsStudent} →
                      </button>
                    )}
                    {/* Primary: Create new tRAT */}
                    <button
                      onClick={onTeacher}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: G.gold, color: G.onGold, border: "none",
                        borderRadius: 8, padding: "9px 16px", fontSize: 14, fontWeight: 600,
                        fontFamily: "'DM Sans',sans-serif", cursor: "pointer",
                        transition: "filter 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.08)"}
                      onMouseLeave={e => e.currentTarget.style.filter = "none"}
                    >
                      + {t.createSession}
                    </button>
                  </div>
                }
                G={G}
              />

              {/* Onboarding welcome — shown when the teacher has no tRATs yet
                  (only after the first load resolves, so it doesn't flash for
                  returning teachers while sessions are still loading). */}
              {total === 0 && sessionsLoaded && (
                <div style={{
                  background: G.surface,
                  border: `1px solid ${G.gold}44`,
                  borderRadius: 12,
                  padding: "28px 32px",
                  marginBottom: 32,
                  maxWidth: 640,
                }}>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: G.gold, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10 }}>
                    {lang === "sv" ? "Kom igång" : "Get started"}
                  </div>
                  <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 22, fontWeight: 700, color: G.text, margin: "0 0 10px", letterSpacing: "-0.01em" }}>
                    {lang === "sv" ? `Välkommen${firstName ? `, ${firstName}` : ""}!` : `Welcome${firstName ? `, ${firstName}` : ""}!`}
                  </h2>
                  <p style={{ fontSize: 15, color: G.sub, lineHeight: 1.6, margin: "0 0 18px" }}>
                    {lang === "sv"
                      ? <>Skapa din första tRAT via <strong style={{ color: G.text }}>+ Skapa ny tRAT</strong> uppe till höger — eller utforska tRAT:er som kollegor har delat via <strong style={{ color: G.text }}>Mina kurser</strong> i vänstermenyn.</>
                      : <>Create your first tRAT with <strong style={{ color: G.text }}>+ Create tRAT</strong> in the top right — or browse tRATs your colleagues have shared via <strong style={{ color: G.text }}>My courses</strong> in the sidebar.</>
                    }
                  </p>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      onClick={onTeacher}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: G.gold, color: G.onGold, border: "none",
                        borderRadius: 8, padding: "9px 16px", fontSize: 14, fontWeight: 600,
                        fontFamily: "'DM Sans',sans-serif", cursor: "pointer",
                        transition: "filter 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.08)"}
                      onMouseLeave={e => e.currentTarget.style.filter = "none"}
                    >
                      + {t.createSession}
                    </button>
                    <button
                      onClick={() => navigate("/courses")}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: "transparent", color: G.text,
                        border: `1px solid ${G.border}`,
                        borderRadius: 8, padding: "9px 14px", fontSize: 14, fontWeight: 500,
                        fontFamily: "'DM Sans',sans-serif", cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = G.text; e.currentTarget.style.background = G.surface2; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = G.border; e.currentTarget.style.background = "transparent"; }}
                    >
                      <BookOpen size={14} />
                      {lang === "sv" ? "Mina kurser" : "My courses"} →
                    </button>
                  </div>
                </div>
              )}

              {/* Pågår nu + Senast avslutad */}
              {total > 0 && (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: 16, marginBottom: 32,
                }}>
                  <div style={{ background: G.surface, border: `1px solid ${liveNow.length > 0 ? G.gold + "55" : G.border}`, borderRadius: 12, padding: "16px 20px" }}>
                    <div style={heroLabel(liveNow.length > 0 ? G.gold : G.muted)}>
                      {liveNow.length > 0 ? "● " : "○ "}{lang === "sv" ? "Pågår nu" : "Live now"}
                    </div>
                    {liveNow.length > 0 ? (
                      <div>
                        {liveNow.slice(0, 4).map((s, i) => {
                          const act = groupActivity[s.sessionId] || {};
                          const totalGroups = Math.max(s.config?.predefined_groups?.length || 0, (act.active || 0) + (act.done || 0));
                          const appeals = appealCounts[s.sessionId] || 0;
                          const extra = [
                            lang === "sv" ? `${act.done || 0} av ${totalGroups} grupper klara` : `${act.done || 0} of ${totalGroups} groups done`,
                            appeals > 0 ? `${appeals} ${appeals === 1 ? "appeal" : "appeals"}` : null,
                            s.teacherId !== user.id ? (teacherNames[s.sessionId] || (lang === "sv" ? "Kollega" : "Colleague")) : null,
                          ].filter(Boolean).join(" · ");
                          return (
                            <button key={s.sessionId} onClick={() => onDashboard(s.dashId)} style={heroRow(i > 0, G)}>
                              <span style={{ minWidth: 0 }}>
                                <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: G.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.sessionTitle || `tRAT ${s.sessionId}`}</span>
                                <span style={{ display: "block", fontSize: 12, color: G.muted, marginTop: 2 }}>{extra}</span>
                              </span>
                              <span style={{ fontSize: 13, color: G.gold, fontWeight: 600, whiteSpace: "nowrap" }}>Dashboard →</span>
                            </button>
                          );
                        })}
                        {liveNow.length > 4 && (
                          <div style={{ fontSize: 12, color: G.muted, marginTop: 6 }}>
                            {lang === "sv" ? `+ ${liveNow.length - 4} till i listan nedan` : `+ ${liveNow.length - 4} more in the list below`}
                          </div>
                        )}
                      </div>
                    ) : upcoming ? (
                      <button onClick={() => onDashboard(upcoming.dashId)} style={heroRow(false, G)}>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 12, color: G.muted }}>{lang === "sv" ? "Nästa" : "Next"}</span>
                          <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: G.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{upcoming.sessionTitle || `tRAT ${upcoming.sessionId}`}</span>
                        </span>
                        <span style={{ fontSize: 13, color: G.sub, whiteSpace: "nowrap" }}>
                          {new Date(upcoming.availableAt).toLocaleString(lang === "sv" ? "sv-SE" : "en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </button>
                    ) : (
                      <div style={{ fontSize: 14, color: G.muted, fontStyle: "italic" }}>
                        {lang === "sv" ? "Inget pågår just nu" : "Nothing running right now"}
                      </div>
                    )}
                  </div>

                  <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: "16px 20px" }}>
                    <div style={heroLabel(G.muted)}>{lang === "sv" ? "Senast avslutad" : "Last finished"}</div>
                    {lastDone && lastSummary?.sessionId === lastDone.sessionId ? (
                      <button onClick={() => onDashboard(lastDone.dashId)} style={{ display: "block", width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans',sans-serif" }}>
                        <div style={{ fontSize: 13, color: G.sub, marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {lastDone.sessionTitle || `tRAT ${lastDone.sessionId}`} · {new Date(lastDone.availableAt || lastDone.createdAt).toLocaleDateString(lang === "sv" ? "sv-SE" : "en-GB", { day: "numeric", month: "short" })}
                        </div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                          <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 28, fontWeight: 700, color: G.text, letterSpacing: "-0.02em" }}>{lastSummary.avgPct} %</span>
                          <span style={{ fontSize: 13, color: G.muted }}>
                            {lang === "sv" ? `snitt för ${lastSummary.groupCount} ${lastSummary.groupCount === 1 ? "grupp" : "grupper"}` : `average of ${lastSummary.groupCount} group${lastSummary.groupCount === 1 ? "" : "s"}`}
                          </span>
                        </div>
                        {/* En stapel per grupp, högst först */}
                        <div role="img" aria-label={lastSummary.groups.map(g => `${g.name} ${g.pct} %`).join(", ")}
                          style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 32, margin: "10px 0 10px" }}>
                          {lastSummary.groups.map(g => (
                            <div key={g.name} title={`${g.name}: ${g.pct} %`} style={{ flex: 1, maxWidth: 28, height: `${Math.max(6, g.pct)}%`, background: scoreColor(g.pct / 100, G), borderRadius: 2 }} />
                          ))}
                        </div>
                        {lastSummary.hardest && (
                          <div style={{ fontSize: 13, color: G.muted, borderTop: `1px solid ${G.border}`, paddingTop: 8 }}>
                            {lang === "sv" ? "Svåraste frågan: " : "Hardest question: "}
                            <span style={{ color: G.text, fontWeight: 600 }}>{lang === "sv" ? "fråga" : "question"} {lastSummary.hardest.qi + 1}</span>
                            {" — "}
                            {lang === "sv"
                              ? `${lastSummary.hardest.firstTry} av ${lastSummary.groupCount} rätt på första försöket`
                              : `${lastSummary.hardest.firstTry} of ${lastSummary.groupCount} right on the first try`}
                          </div>
                        )}
                      </button>
                    ) : (
                      <div style={{ fontSize: 14, color: G.muted, fontStyle: "italic" }}>
                        {lastDone ? "…" : (lang === "sv" ? "Ingen tRAT har körts klart än" : "No tRAT has finished yet")}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* Two Cards - Student & Teacher — visible only on the public landing (not logged in) */}
        {!user && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24, marginBottom: 64 }}>
            {/* Student Card */}
            <div
              style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 18, padding: "32px", boxShadow: theme === 'dark' ? 'none' : '0 2px 8px rgba(0,0,0,0.04)', transition: "transform 0.2s" }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: 12, background: `${G.accent}15`, marginBottom: 20, transition: "transform 0.2s" }}>
                <Users size={32} color={G.accent} />
              </div>
              <h3 style={{ ...css.heading, fontSize: 18, marginBottom: 8, color: G.text }}>{t.forStudents}</h3>
              <p style={{ color: G.sub, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>{t.forStudentsCard}</p>
              <label style={css.label}><LabelText text={t.enterSessionCode} /></label>
              <input
                style={{ ...css.input, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 18, padding: "12px 16px", textAlign: "center" }}
                placeholder={lang === "sv" ? "t.ex. A3F9BC12" : "e.g. A3F9BC12"}
                value={groupCode}
                onChange={e => setGroupCode(e.target.value.toUpperCase())}
                maxLength={8}
                onKeyDown={e => e.key === "Enter" && onGroup(groupCode.trim())}
              />
              <button style={{ ...css.btnPrimary, width: "100%", background: G.accent, marginTop: 4 }} onClick={() => onGroup(groupCode.trim())}>{t.join}</button>
            </div>

            {/* Teacher Card */}
            <div
              style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 18, padding: "32px", boxShadow: theme === 'dark' ? 'none' : '0 2px 8px rgba(0,0,0,0.04)', transition: "transform 0.2s" }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: 12, background: `${G.gold}15`, marginBottom: 20, transition: "transform 0.2s" }}>
                <LayoutDashboard size={32} color={G.gold} />
              </div>
              <h3 style={{ ...css.heading, fontSize: 18, marginBottom: 8, color: G.text }}>{t.forTeachers}</h3>
              <p style={{ color: G.sub, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>{t.forTeachersCard}</p>
              {/* Primary action only for unauth (sign in). For onboarding teacher, header already has create button. */}
              {!user && (
                <>
                  {/* Outline button — visually secondary to the student "Gå med" CTA (Task 5) */}
                  <button
                    onClick={onTeacher}
                    style={{
                      width: "100%",
                      background: "transparent",
                      color: G.gold,
                      border: `1px solid ${G.gold}`,
                      borderRadius: 12,
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 15, fontWeight: 700, letterSpacing: "0.03em",
                      padding: "13px 20px", cursor: "pointer",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = `${G.gold}10`}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    {t.signIn} →
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Student modal — for logged-in teachers who want to join a tRAT as a student */}
        {showStudentModal && (
          <div
            onClick={() => setShowStudentModal(false)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 20, zIndex: 200,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: G.surface, border: `1px solid ${G.border}`,
                borderRadius: 14, padding: "28px 32px",
                maxWidth: 420, width: "100%",
                boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <h3 style={{ ...css.heading, fontSize: 20, margin: 0, color: G.text }}>{t.joinAsStudent}</h3>
                <button onClick={() => setShowStudentModal(false)} style={{ background: "none", border: "none", color: G.muted, fontSize: 18, cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
              </div>
              <p style={{ color: G.sub, fontSize: 13, marginBottom: 18, lineHeight: 1.5 }}>
                {lang === "sv" ? "Skriv in en tRAT-kod du fått från en lärare." : "Enter a tRAT code given to you by a teacher."}
              </p>
              <label style={css.label}><LabelText text={t.enterSessionCode} /></label>
              <input
                autoFocus
                style={{ ...css.input, marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 16, padding: "12px 16px", textAlign: "center" }}
                placeholder={lang === "sv" ? "t.ex. A3F9BC12" : "e.g. A3F9BC12"}
                value={studentModalCode}
                onChange={e => setStudentModalCode(e.target.value.toUpperCase())}
                maxLength={8}
                onKeyDown={e => {
                  if (e.key === "Enter" && studentModalCode.trim()) {
                    onGroup(studentModalCode.trim());
                    setShowStudentModal(false);
                  } else if (e.key === "Escape") {
                    setShowStudentModal(false);
                  }
                }}
              />
              <button
                style={{ ...css.btnPrimary, width: "100%", background: G.accent }}
                onClick={() => {
                  if (studentModalCode.trim()) {
                    onGroup(studentModalCode.trim());
                    setShowStudentModal(false);
                  }
                }}
                disabled={!studentModalCode.trim()}
              >
                {t.join}
              </button>
            </div>
          </div>
        )}

        {/* Dashboard code modal — extracted from teacher card (Task 1) */}
        {showDashboardModal && (
          <div
            onClick={() => setShowDashboardModal(false)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 20, zIndex: 200,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: G.surface, border: `1px solid ${G.border}`,
                borderRadius: 14, padding: "28px 32px",
                maxWidth: 420, width: "100%",
                boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <h3 style={{ ...css.heading, fontSize: 20, margin: 0, color: G.text }}>{t.haveDashboardCode}</h3>
                <button onClick={() => setShowDashboardModal(false)} style={{ background: "none", border: "none", color: G.muted, fontSize: 18, cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
              </div>
              <p style={{ color: G.sub, fontSize: 13, marginBottom: 18, lineHeight: 1.5 }}>
                {lang === "sv" ? "Skriv in dashboardkoden från en tRAT du redan skapat." : "Enter the dashboard code from a tRAT you already created."}
              </p>
              <label style={css.label}>{t.enterDashCode}</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  autoFocus
                  style={{ ...css.input, flex: 1, textTransform: "uppercase", letterSpacing: "0.08em" }}
                  placeholder="DASH-..."
                  value={dashboardModalCode}
                  onChange={e => setDashboardModalCode(e.target.value.toUpperCase())}
                  onKeyDown={e => {
                    if (e.key === "Enter" && dashboardModalCode.trim()) {
                      onDashboard(dashboardModalCode.trim());
                      setShowDashboardModal(false);
                    } else if (e.key === "Escape") {
                      setShowDashboardModal(false);
                    }
                  }}
                />
                <button
                  style={{ ...css.btnPrimary, padding: "10px 16px" }}
                  onClick={() => {
                    if (dashboardModalCode.trim()) {
                      onDashboard(dashboardModalCode.trim());
                      setShowDashboardModal(false);
                    }
                  }}
                  disabled={!dashboardModalCode.trim()}
                >
                  {t.view}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* tRATs — alla i mina kurser, med kurs-, ägar- och statusfilter */}
        {user && mySessions.length > 0 && (() => {
          const statusFor = statusForSession;
          const ctx = {
            userId: user.id,
            statusFor,
            instanceCourse,
            searchText: s => [
              s.sessionTitle || "",
              s.sessionId || "",
              s.dashId || "",
              courseLabelById[s.courseInstanceId] || "",
              teacherNames[s.sessionId] || "",
            ].join(" "),
          };
          const courses = courseOptions(mySessions, instanceCourse);
          // Ett sparat kursval som inte längre finns (kursen borttagen, rollen
          // ändrad) får inte lämna listan tom utan förklaring.
          const course = courses.some(c => c.id === courseFilter) ? courseFilter : "all";
          const hasColleagues = mySessions.some(s => s.teacherId !== user.id);
          const owner = hasColleagues ? ownerFilter : "all";

          // Statusräknarna följer kurs- och ägarvalet men inte statusvalet
          const scoped = filterSessions(mySessions, { course, owner }, ctx);
          const STATUS_TABS = [
            { id: "all", label: lang === "sv" ? "Alla" : "All", color: G.text },
            ...SESSION_STATUSES.map(id => {
              const m = statusMeta(id, lang, G);
              return { id, label: m.labelPlural, color: m.color };
            }),
          ];
          const counts = scoped.reduce((acc, s) => {
            acc[statusFor(s)] = (acc[statusFor(s)] || 0) + 1;
            return acc;
          }, { all: scoped.filter(s => statusFor(s) !== "locked").length });

          const filtered = sortSessions(
            filterSessions(scoped, { status: statusFilter, q: searchQuery }, ctx),
            statusFor,
          );
          const shown = filtered.slice(0, shownCount);

          const chip = (active, color = G.text) => ({
            display: "inline-flex", alignItems: "center", gap: 6,
            background: active ? color + "18" : "transparent",
            border: `1px solid ${active ? color + "55" : G.border}`,
            borderRadius: 999,
            color: active ? color : G.muted,
            fontSize: 12, fontWeight: active ? 600 : 500,
            padding: "5px 12px",
            cursor: "pointer",
            fontFamily: "'DM Sans',sans-serif",
            transition: "all 0.15s",
          });

          return (
            <div id="my-sessions" data-tour="trat-list" style={{ marginBottom: 40 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
                <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 16, fontWeight: 600, color: G.text, margin: 0, letterSpacing: "-0.005em" }}>
                  tRATs
                </h2>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: G.muted }}>
                  {filtered.length} / {mySessions.length}
                </span>
              </div>

              {/* Kurs och skapare */}
              {(courses.length > 1 || hasColleagues) && (
                <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
                  {courses.length > 1 && (
                    <select
                      aria-label={lang === "sv" ? "Kurs" : "Course"}
                      value={course}
                      onChange={e => setCourseFilter(e.target.value)}
                      style={{ ...css.input, marginBottom: 0, width: "auto", minWidth: 200, maxWidth: "100%", fontSize: 14, padding: "8px 12px" }}
                    >
                      <option value="all">{lang === "sv" ? "Alla kurser" : "All courses"}</option>
                      {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                  {hasColleagues && (
                    <div role="group" aria-label={lang === "sv" ? "Skapad av" : "Created by"} style={{ display: "flex", gap: 4 }}>
                      {[
                        { id: "all", label: lang === "sv" ? "Alla" : "All" },
                        { id: "mine", label: lang === "sv" ? "Mina" : "Mine" },
                      ].map(o => (
                        <button key={o.id} aria-pressed={owner === o.id} onClick={() => setOwnerFilter(o.id)} style={chip(owner === o.id)}>
                          {o.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Search input */}
              <div style={{ position: "relative", marginBottom: 12 }}>
                <Search size={14} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: G.muted, pointerEvents: "none" }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder={lang === "sv" ? "Sök tRAT efter namn, kod, kurs eller lärare…" : "Search tRAT by name, code, course or teacher…"}
                  style={{
                    ...css.input,
                    paddingLeft: 36,
                    fontSize: 14,
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: G.muted, cursor: "pointer", padding: 4, lineHeight: 1 }}
                    aria-label="Clear"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Status filter tabs */}
              <div style={{ display: "flex", gap: 4, marginBottom: 16, flexWrap: "wrap" }}>
                {STATUS_TABS.map(tab => {
                  const active = statusFilter === tab.id;
                  const count = counts[tab.id] || 0;
                  if (tab.id !== "all" && count === 0) return null;
                  return (
                    <button key={tab.id} onClick={() => setStatusFilter(tab.id)} style={chip(active, tab.color)}>
                      {tab.id !== "all" && <span style={{ width: 6, height: 6, borderRadius: "50%", background: tab.color }} />}
                      {tab.label}
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, opacity: 0.7 }}>{count}</span>
                    </button>
                  );
                })}
              </div>

              {filtered.length === 0 ? (
                <div style={{ background: G.surface, border: `1px dashed ${G.border}`, borderRadius: 12, padding: 28, color: G.muted, textAlign: "center", fontSize: 14 }}>
                  {lang === "sv" ? "Inga tRAT:er matchar dina filter." : "No tRATs match your filters."}
                </div>
              ) : (
                <>
                  <TeacherSessionList
                    sessions={shown}
                    onRefresh={loadSessions}
                    onDashboard={onDashboard}
                    courseLabelById={courseLabelById}
                    teacherNames={teacherNames}
                    appealCounts={appealCounts}
                    lang={lang}
                    G={G}
                    css={css}
                  />
                  {filtered.length > shown.length && (
                    <div style={{ textAlign: "center", marginTop: 14 }}>
                      <button
                        onClick={() => setShownCount(n => n + PAGE_SIZE)}
                        style={{ background: "transparent", border: `1px solid ${G.border}`, borderRadius: 8, color: G.text, fontSize: 13, padding: "8px 16px", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}
                      >
                        {lang === "sv" ? `Visa fler (${filtered.length - shown.length} till)` : `Show more (${filtered.length - shown.length} more)`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* How it works - visas bara för ej inloggade */}
        {!user && (
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <h2 style={{ ...css.heading, fontSize: 24, marginBottom: 40, color: G.text }}>{t.howItWorks}</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 32 }}>
              {/* Step 1 - Guld */}
              <div style={{ textAlign: "center" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: "50%", background: G.surface, border: `2px solid ${G.gold}`, margin: "0 auto 16px" }}>
                  <FilePlus size={28} color={G.gold} />
                </div>
                <h4 style={{ ...css.heading, fontSize: 16, marginBottom: 8, color: G.text }}>{t.step1Title}</h4>
                <p style={{ color: G.sub, fontSize: 14, lineHeight: 1.5 }}>{t.step1Text}</p>
              </div>

              {/* Step 2 - Grön */}
              <div style={{ textAlign: "center" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: "50%", background: G.surface, border: `2px solid ${G.accent}`, margin: "0 auto 16px" }}>
                  <Key size={28} color={G.accent} />
                </div>
                <h4 style={{ ...css.heading, fontSize: 16, marginBottom: 8, color: G.text }}>{t.step2Title}</h4>
                <p style={{ color: G.sub, fontSize: 14, lineHeight: 1.5 }}>{t.step2Text}</p>
              </div>

              {/* Step 3 - Blå */}
              <div style={{ textAlign: "center" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: "50%", background: G.surface, border: `2px solid ${G.accent}`, margin: "0 auto 16px" }}>
                  <BarChart3 size={28} color={G.accent} />
                </div>
                <h4 style={{ ...css.heading, fontSize: 16, marginBottom: 8, color: G.text }}>{t.step3Title}</h4>
                <p style={{ color: G.sub, fontSize: 14, lineHeight: 1.5 }}>{t.step3Text}</p>
              </div>
            </div>

            {/* Discreet quick-links: dashboard code + What is tRAT (Task 1 footer) */}
            <div style={{ marginTop: 40, display: "flex", justifyContent: "center", alignItems: "center", gap: 12, flexWrap: "wrap", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
              <button
                onClick={() => { setDashboardModalCode(""); setShowDashboardModal(true); }}
                style={{ background: "none", border: "none", padding: 0, color: G.muted, fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textDecoration: "underline", textUnderlineOffset: 3 }}
                onMouseEnter={e => e.currentTarget.style.color = G.text}
                onMouseLeave={e => e.currentTarget.style.color = G.muted}
              >
                {t.haveDashboardCode} →
              </button>
              <span style={{ color: G.border }}>·</span>
              <button
                onClick={() => setShowModal(true)}
                style={{ background: "none", border: "none", padding: 0, color: G.muted, fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textDecoration: "underline", textUnderlineOffset: 3 }}
                onMouseEnter={e => e.currentTarget.style.color = G.text}
                onMouseLeave={e => e.currentTarget.style.color = G.muted}
              >
                {t.whatIsTRAT}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal - Utökad med steg */}
      {showModal && (
        <div 
          style={{ 
            position: "fixed", 
            inset: 0, 
            background: "rgba(0,0,0,0.5)", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            padding: 20,
            zIndex: 10000
          }}
          onClick={() => setShowModal(false)}
        >
          <div 
            style={{ 
              background: G.surface, 
              border: `1px solid ${G.border}`, 
              borderRadius: 18, 
              padding: "32px", 
              maxWidth: 560, 
              width: "100%",
              position: "relative",
              maxHeight: "85vh",
              overflowY: "auto"
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setShowModal(false)}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 8,
                color: G.muted
              }}
            >
              <X size={20} />
            </button>
            <h2 style={{ ...css.heading, fontSize: 24, marginBottom: 16, color: G.text }}>{t.whatIsTRATTitle}</h2>
            <div style={{ color: G.text, fontSize: 14, lineHeight: 1.7 }}>
              {lang === 'sv' ? (
                <>
                  <p style={{ marginBottom: 14, color: G.sub }}>tRAT (Team Readiness Assurance Test) är ett centralt moment inom Team-Based Learning (TBL). Syftet är att säkerställa att studentgrupper har tillräckliga förkunskaper för att kunna gå vidare till tillämpningsövningar, samtidigt som det fördjupar förståelsen genom gruppdiskussion.</p>
                  <p style={{ fontWeight: 600, color: G.text, marginBottom: 6 }}>Metodik och poängsättning</p>
                  <p style={{ marginBottom: 8, color: G.sub }}>Studenterna löser flervalsuppgifter tillsammans och får omedelbar återkoppling på sina val genom en digital motsvarighet till IF-AT-skraplotter.</p>
                  <ul style={{ paddingLeft: 18, marginBottom: 14, color: G.sub }}>
                    <li style={{ marginBottom: 5 }}><strong style={{ color: G.text, opacity: 1 }}>Omedelbar återkoppling:</strong> Gruppen ser direkt om ett svar är korrekt eller inte.</li>
                    <li style={{ marginBottom: 5 }}><strong style={{ color: G.text, opacity: 1 }}>Flera försök:</strong> Vid felaktigt svar diskuterar gruppen vidare och gör ett nytt försök.</li>
                    <li><strong style={{ color: G.text, opacity: 1 }}>Viktad poängsättning:</strong> Full poäng ges vid första försöket, därefter minskar poängen för varje försök. Detta uppmuntrar till konsensus och välgrundade argument framför gissningar.</li>
                  </ul>
                  <p style={{ fontWeight: 600, color: G.text, marginBottom: 6 }}>Fördelar för ditt lärande</p>
                  <ul style={{ paddingLeft: 18, margin: 0, color: G.sub }}>
                    <li style={{ marginBottom: 5 }}><strong style={{ color: G.text, opacity: 1 }}>Aktivt samarbete:</strong> Metoden kräver interaktion och att ni verbaliserar era kunskaper, vilket stärker minneshanteringen.</li>
                    <li style={{ marginBottom: 5 }}><strong style={{ color: G.text, opacity: 1 }}>Direkt korrigering:</strong> Eventuella missförstånd identifieras och rättas till direkt under pågående moment, när ni är som mest mottagliga för förklaringar.</li>
                    <li><strong style={{ color: G.text, opacity: 1 }}>Behovsanpassat stöd:</strong> Läraren ser resultaten i realtid och kan anpassa efterföljande genomgångar efter de områden där grupper har haft svårast att nå konsensus.</li>
                  </ul>
                </>
              ) : (
                <>
                  <p style={{ marginBottom: 14, color: G.sub }}>tRAT (Team Readiness Assurance Test) is a core component of Team-Based Learning (TBL). Its purpose is to ensure that student groups have sufficient prior knowledge to proceed to application exercises, while deepening understanding through group discussion.</p>
                  <p style={{ fontWeight: 600, color: G.text, marginBottom: 6 }}>Method &amp; scoring</p>
                  <p style={{ marginBottom: 8, color: G.sub }}>Students solve multiple-choice questions together and receive immediate feedback through a digital equivalent of IF-AT scratch cards.</p>
                  <ul style={{ paddingLeft: 18, marginBottom: 14, color: G.sub }}>
                    <li style={{ marginBottom: 5 }}><strong style={{ color: G.text, opacity: 1 }}>Immediate feedback:</strong> The group sees right away whether an answer is correct or not.</li>
                    <li style={{ marginBottom: 5 }}><strong style={{ color: G.text, opacity: 1 }}>Multiple attempts:</strong> If the answer is wrong, the group discusses further and tries again.</li>
                    <li><strong style={{ color: G.text, opacity: 1 }}>Weighted scoring:</strong> Full points are awarded on the first attempt; points decrease with each subsequent attempt. This encourages consensus and reasoned arguments over guessing.</li>
                  </ul>
                  <p style={{ fontWeight: 600, color: G.text, marginBottom: 6 }}>Benefits for your learning</p>
                  <ul style={{ paddingLeft: 18, margin: 0, color: G.sub }}>
                    <li style={{ marginBottom: 5 }}><strong style={{ color: G.text, opacity: 1 }}>Active collaboration:</strong> The method requires interaction and verbalising your knowledge, which strengthens retention.</li>
                    <li style={{ marginBottom: 5 }}><strong style={{ color: G.text, opacity: 1 }}>Immediate correction:</strong> Misunderstandings are identified and addressed on the spot, when you are most receptive to explanations.</li>
                    <li><strong style={{ color: G.text, opacity: 1 }}>Targeted support:</strong> The teacher sees results in real time and can tailor follow-up sessions to the areas where groups struggled most to reach consensus.</li>
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer G={G} />
    </div>
  );
}

export { HomeScreen };
